import React, { useEffect, useState } from 'react';
import { Save, Eye, Download, Send } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

const TAX = [['gst', 'GST only'], ['gst_pst', 'GST + PST/QST'], ['pst', 'PST/QST only'], ['none', 'No tax']];
const ACCTS = [['inventory_account', 'Stock items account'], ['default_expense_account', 'Default expense account (non-stock)'], ['freight_account', 'Freight account']];
const lastMonth = () => { const d = new Date(); d.setDate(0); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`; };

const openBlob = async (api, month, pdf) => {
  const r = await api().get('/po-accounting/register', { params: { month, format: pdf ? 'pdf' : 'html' }, responseType: pdf ? 'blob' : 'text' });
  if (pdf) { const u = URL.createObjectURL(r.data); const a = document.createElement('a'); a.href = u; a.download = `PO-Register-${month}.pdf`; a.click(); URL.revokeObjectURL(u); return; }
  const w = window.open('', '_blank'); if (!w) { toast.error('Pop-up blocked'); return; } w.document.write(r.data); w.document.close();
};

export const PoAccountingSettings = ({ api }) => {
  const [cfg, setCfg] = useState(null);
  const [emails, setEmails] = useState('');
  const [month, setMonth] = useState(lastMonth());
  const [runs, setRuns] = useState([]);
  const loadRuns = () => api().get('/admin/purchase-orders/register/runs').then(r => setRuns(r.data)).catch(() => {});
  useEffect(() => {
    api().get('/po-accounting/settings').then(r => { setCfg(r.data); setEmails(r.data.register_emails.join(', ')); }).catch(() => {});
    loadRuns();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (!cfg) return null;

  const save = async () => {
    try {
      const r = await api().put('/admin/purchase-orders/accounting-settings', { ...cfg, default_due_days: Number(cfg.default_due_days) || 0, register_emails: emails.split(/[,;\s]+/).filter(Boolean) });
      setCfg(r.data); setEmails(r.data.register_emails.join(', ')); toast.success('Accounting settings saved');
    } catch (e) { const d = e.response?.data?.detail; toast.error(Array.isArray(d) ? d.map(x => x.msg).join('; ') : (d || 'Save failed')); }
  };
  const sendNow = async () => {
    try { const r = await api().post('/admin/purchase-orders/register/send', null, { params: { month } }); toast.success(`Register for ${month} sent to ${r.data.to.join(', ')}`); }
    catch (e) { toast.error(e.response?.data?.detail || 'Send failed'); }
    loadRuns();
  };
  const setTax = (k, v) => setCfg({ ...cfg, tax_codes: { ...cfg.tax_codes, [k]: v } });

  return (
    <Card className="industrial-card" data-testid="po-accounting-card">
      <CardHeader className="border-b border-slate-100 pb-3"><CardTitle className="text-lg">QuickBooks Mapping & Monthly Register</CardTitle></CardHeader>
      <CardContent className="p-4 space-y-5 text-sm">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5">Tax codes (type them exactly as named in QuickBooks)</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">{TAX.map(([k, l]) => (
            <div key={k}><Label className="text-xs">{l}</Label><Input value={cfg.tax_codes[k]} onChange={(e) => setTax(k, e.target.value)} className="h-8 rounded-sm mono" data-testid={`qb-tax-${k}`} /></div>
          ))}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5">Accounts</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {ACCTS.map(([k, l]) => <div key={k}><Label className="text-xs">{l}</Label><Input value={cfg[k]} onChange={(e) => setCfg({ ...cfg, [k]: e.target.value })} className="h-8 rounded-sm" data-testid={`qb-${k.replace(/_/g, '-')}`} /></div>)}
            <div><Label className="text-xs">Due days when vendor has no "Net N" terms</Label><Input type="number" min="0" value={cfg.default_due_days} onChange={(e) => setCfg({ ...cfg, default_due_days: e.target.value })} className="h-8 rounded-sm mono" data-testid="qb-default-due-days" /></div>
          </div>
          <p className="text-[11px] text-slate-500 mt-1">A PO type's GL / cost code (below) overrides the default expense account for non-stock lines of that type.</p>
        </div>
        <div className="border-t border-slate-100 pt-4 space-y-2">
          <label className="flex items-center gap-2"><input type="checkbox" className="h-4 w-4" checked={cfg.register_enabled} onChange={(e) => setCfg({ ...cfg, register_enabled: e.target.checked })} data-testid="qb-register-enabled" /> Email last month's PO Register PDF on the 1st of every month</label>
          <div><Label className="text-xs">Extra recipients (the PO email always gets it)</Label><Input value={emails} onChange={(e) => setEmails(e.target.value)} placeholder="accountant@example.com, controller@example.com" className="h-8 rounded-sm" data-testid="qb-register-emails" /></div>
        </div>
        <div className="flex gap-2 flex-wrap items-end">
          <Button size="sm" onClick={save} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="qb-settings-save"><Save className="w-3.5 h-3.5 mr-1" /> Save</Button>
          <span className="mx-2 h-6 w-px bg-slate-200" />
          <div><Label className="text-xs">Register month</Label><Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="h-8 w-40 rounded-sm" data-testid="qb-register-month" /></div>
          <Button size="sm" variant="outline" onClick={() => openBlob(api, month, false).catch(() => toast.error('Preview failed'))} className="rounded-sm" data-testid="qb-register-preview"><Eye className="w-3.5 h-3.5 mr-1" /> Preview</Button>
          <Button size="sm" variant="outline" onClick={() => openBlob(api, month, true).catch(() => toast.error('Download failed'))} className="rounded-sm" data-testid="qb-register-pdf"><Download className="w-3.5 h-3.5 mr-1" /> PDF</Button>
          <Button size="sm" variant="outline" onClick={sendNow} className="rounded-sm" data-testid="qb-register-send"><Send className="w-3.5 h-3.5 mr-1" /> Send now</Button>
        </div>
        {runs.length > 0 && (
          <div className="text-xs text-slate-600" data-testid="qb-register-runs">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Recent sends</div>
            {runs.map((r, i) => <div key={i}>{r.at.slice(0, 16).replace('T', ' ')} · {r.month} · {r.po_count} POs · {r.status === 'sent' ? 'sent' : `failed: ${r.error || ''}`} · {r.to.join(', ')}</div>)}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PoAccountingSettings;
